import React, { useCallback, useEffect, useMemo, useState } from "react";

import { apiDownload, apiGet, apiPost } from "../../Api/Client.js";
import { createLatestGate } from "../../Shared/latestRequest.js";
import { Badge, Button, Card, Page, PageHeader, Table } from "../../Ui/index.jsx";

// Audit portal (plugin platform S9). Only an auditor with an active engagement sees it.
// First screen: one sentence and five tiles; everything else is one click away.
// Everything an auditor does here is itself recorded, and nothing here can change
// business data. A distinct band says "audit mode" so nobody mistakes where they are.

const STREAMS = [
  { value: "", label: "Everything" },
  { value: "R,X", label: "Looked at sensitive data, exports" },
  { value: "A", label: "Access changes" },
  { value: "W", label: "Changes" },
  { value: "AU", label: "Auditor activity" },
  { value: "T", label: "Trail upkeep" },
];
const TILE_FILTER = { unusual: "R,X", sensitive: "R,X", access: "A", agents: "", health: "T" };
const TABS = ["Overview", "Trail", "Settings", "Trail health"];

function errText(err, fallback) {
  return err?.payload?.message || err?.payload?.detail || err?.message || fallback;
}

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function detailText(detail) {
  if (!detail) return "";
  const parts = [];
  if (detail.fields?.length) parts.push(`fields: ${detail.fields.join(", ")}`);
  if (detail.rows != null) parts.push(`${detail.rows} record${detail.rows === 1 ? "" : "s"}`);
  if (detail.kind) parts.push(detail.kind.replace(/_/g, " "));
  if (detail.before !== undefined || detail.after !== undefined) {
    parts.push(`before: ${JSON.stringify(detail.before)} → after: ${JSON.stringify(detail.after)}`);
  }
  if (detail.setting) parts.push(`setting: ${detail.setting}`);
  return parts.join(" · ");
}

function Overview({ summary, onOpen }) {
  if (!summary) return null;
  return (
    <>
      <p style={{ fontSize: 18, margin: "8px 0 20px" }}>{summary.sentence}</p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
        {summary.tiles.map((t) => (
          <Card key={t.key} title={t.title}>
            <div style={{ fontSize: 32, fontWeight: 600, margin: "4px 0 8px" }}>{t.count}</div>
            <p className="ds-muted" style={{ minHeight: 40 }}>{t.line}</p>
            <Button variant="secondary" size="sm" onClick={() => onOpen(t.key)}>
              {t.key === "health" ? "See trail health" : "Look"}
            </Button>
          </Card>
        ))}
      </div>
    </>
  );
}

function Trail({ initialStream }) {
  const [stream, setStream] = useState(initialStream || "");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const query = useCallback(() => {
    const q = new URLSearchParams();
    if (stream) q.set("stream", stream);
    if (from) q.set("from", from);
    if (to) q.set("to", to);
    return q;
  }, [stream, from, to]);

  useEffect(() => { setStream(initialStream || ""); setPage(1); }, [initialStream]);
  // Filters change quickly; a slow earlier answer must not replace the newer one on screen.
  const gate = useMemo(createLatestGate, []);
  useEffect(() => {
    const q = query();
    q.set("page", String(page));
    const ticket = gate.start();
    apiGet(`/Audit/events/?${q.toString()}`)
      .then((res) => { if (gate.isCurrent(ticket)) { setData(res); setError(""); } })
      .catch((err) => { if (gate.isCurrent(ticket)) setError(errText(err, "Could not load the trail.")); });
  }, [query, page, gate]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;
  return (
    <>
      <div className="ds-toolbar" style={{ gap: 12, flexWrap: "wrap", margin: "8px 0 16px" }}>
        <select className="ds-input" style={{ maxWidth: 280 }} value={stream} onChange={(e) => { setStream(e.target.value); setPage(1); }}>
          {STREAMS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <label className="ds-muted">From <input className="ds-input" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} /></label>
        <label className="ds-muted">To <input className="ds-input" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} /></label>
        <span style={{ flex: 1 }} />
        <Button variant="secondary" onClick={() => apiDownload(`/Audit/events/export/?${query().toString()}`, "audit-trail.csv")
          .catch((err) => setError(errText(err, "Could not download the trail.")))}>
          Download as CSV
        </Button>
      </div>
      {error && <p className="ds-field__error">{error}</p>}
      <Table
        columns={[
          { key: "at", header: "When", render: (r) => when(r.at) },
          { key: "streamName", header: "What kind" },
          { key: "who", header: "Who", render: (r) => <>{r.who || "—"}{r.onBehalfOf ? <span className="ds-muted"> (as {r.onBehalfOf})</span> : null}{r.kind === "agent" ? <Badge>agent</Badge> : null}</> },
          { key: "action", header: "Did", render: (r) => <>{r.action}<span className="ds-muted"> {r.record}{r.recordId ? ` #${r.recordId}` : ""}</span></> },
          { key: "outcome", header: "Result", render: (r) => <Badge variant={r.outcome === "ok" ? "success" : r.outcome === "denied" ? "warning" : "danger"}>{r.outcome}</Badge> },
          { key: "detail", header: "Detail", render: (r) => <span className="ds-muted">{detailText(r.detail)}</span> },
        ]}
        rows={data?.events || []}
        loading={!data && !error}
        empty={error ? "Could not load the records." : "Nothing in this period."}
      />
      {data && data.total > data.pageSize && (
        <div className="ds-toolbar" style={{ gap: 8, marginTop: 12 }}>
          <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
          <span className="ds-muted">Page {page} of {pages} ({data.total} records)</span>
          <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      )}
    </>
  );
}

function SettingRow({ item, onSave, busy }) {
  const [value, setValue] = useState(item.value);
  useEffect(() => setValue(item.value), [item.value]);
  // A number box keeps what was typed (an emptied box stays empty); only a filled box can be saved.
  const isNumber = item.kind !== "bool";
  const filled = !isNumber || String(value).trim() !== "";
  const changed = filled && (isNumber ? Number(value) !== item.value : value !== item.value);
  return (
    <tr>
      <td>
        <div>{item.label}</div>
        {item.reason && <div className="ds-muted" style={{ fontSize: 12 }}>{item.reason}</div>}
        {item.help && <div className="ds-muted" style={{ fontSize: 12 }}>{item.help}</div>}
      </td>
      <td>
        {item.kind === "bool" ? (
          item.locked ? <Badge variant="info">Always on</Badge> : (
            <label><input type="checkbox" checked={!!value} onChange={(e) => setValue(e.target.checked)} /> {value ? "On" : "Off"}</label>
          )
        ) : (
          <><input className="ds-input" style={{ width: 110 }} type="number" min={item.floor ?? undefined} max={item.ceiling ?? undefined}
            value={value} onChange={(e) => setValue(e.target.value)} />{item.kind === "days" ? <span className="ds-muted"> days</span> : null}</>
        )}
        {item.floor != null && item.kind !== "bool" && <div className="ds-muted" style={{ fontSize: 12 }}>Never below {item.floor}</div>}
      </td>
      <td className="ds-muted" style={{ fontSize: 12 }}>{item.changedBy ? `${item.changedBy}, ${when(item.changedAt)}` : "Default"}</td>
      <td>{!item.locked && <Button size="sm" disabled={!changed || busy} onClick={() => onSave(item.key, isNumber ? Number(value) : value)}>Save</Button>}</td>
    </tr>
  );
}

function Settings() {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    apiGet("/Audit/settings/").then((res) => { setData(res); setError(""); })
      .catch((err) => setError(errText(err, "Could not load the settings.")));
  }, []);
  useEffect(() => { load(); }, [load]);
  const save = async (key, value) => {
    setBusy(true); setError(""); setNote("");
    try {
      const res = await apiPost("/Audit/settings/", { key, value });
      setNote(res.status === "waiting_for_second_auditor" ? "Saved as a proposal: a lead auditor must approve it, then it applies 24 hours later." : "Saved.");
      load();
    } catch (err) {
      setError(errText(err, "That change was not made."));
    } finally {
      setBusy(false);
    }
  };
  const approve = async (id) => {
    setError(""); setNote("");
    try { await apiPost(`/Audit/settings/proposals/${id}/approve/`, {}); setNote("Approved: it applies in 24 hours."); load(); }
    catch (err) { setError(errText(err, "Not approved.")); }
  };
  if (!data) return error ? <p className="ds-field__error">{error}</p> : null;
  return (
    <>
      <p className="ds-muted">You decide everything above the legal minimum. Nobody, including you, can go below it. Every change is recorded with who made it.</p>
      {error && <p className="ds-field__error">{error}</p>}
      {note && <p>{note}</p>}
      <div className="ds-tablewrap">
        <table className="ds-table">
          <thead><tr><th>Setting</th><th>Value</th><th>Last changed</th><th /></tr></thead>
          <tbody>{data.settings.map((s) => <SettingRow key={s.key} item={s} onSave={save} busy={busy} />)}</tbody>
        </table>
      </div>
      {data.pending.length > 0 && (
        <Card title="Waiting for a second auditor">
          {data.pending.map((p) => (
            <div key={p.id} className="ds-toolbar" style={{ gap: 12 }}>
              <span>{p.key} → {JSON.stringify(p.value)} (by {p.proposedBy}) {p.effectiveAt ? `applies ${when(p.effectiveAt)}` : ""}</span>
              {p.status === "pending" && <Button size="sm" onClick={() => approve(p.id)}>Approve</Button>}
            </div>
          ))}
        </Card>
      )}
    </>
  );
}

function Health() {
  const [data, setData] = useState(null);
  const [check, setCheck] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    apiGet("/Audit/health/").then(setData).catch((err) => setError(errText(err, "Could not load trail health.")));
  }, []);
  const verify = async () => {
    setBusy(true); setError("");
    try { setCheck(await apiPost("/Audit/health/verify/", {})); }
    catch (err) { setError(errText(err, "The check did not run.")); }
    finally { setBusy(false); }
  };
  return (
    <>
      {data && (
        <Card title={data.sentence}>
          <p className="ds-muted">Last sealed: {data.lastSealed || "not yet"} · Writes that failed to record (whole platform): {data.failedWritesPlatformWide} · Rows waiting to be chained for over 10 minutes: {data.unchainedRowsOlderThan10Minutes}</p>
          {data.clock && (
            <p className="ds-muted">Platform server clock: {data.clock.driftSeconds == null ? "no time server answered" : `${data.clock.driftSeconds}s from ${data.clock.server}`} ({when(data.clock.checkedAt)})
              {" "}{data.clock.ok ? <Badge variant="success">in sync</Badge> : <Badge variant="warning">check</Badge>}</p>
          )}
          {data.problems.length > 0 && (
            <ul>{data.problems.map((p, i) => <li key={i}>Row {p.seq}: {p.problem}</li>)}</ul>
          )}
        </Card>
      )}
      <div style={{ margin: "16px 0" }}>
        <Button onClick={verify} disabled={busy}>{busy ? "Checking…" : "Check the whole trail now"}</Button>
      </div>
      {error && <p className="ds-field__error">{error}</p>}
      {check && (
        <Table
          columns={[
            { key: "streamName", header: "Part of the trail" },
            { key: "rows", header: "Records" },
            { key: "ok", header: "Intact", render: (r) => r.ok ? <Badge variant="success">intact</Badge> : <Badge variant="danger">{r.problems.length} problem(s)</Badge> },
            { key: "problems", header: "Where", render: (r) => r.problems.slice(0, 3).map((p) => `row ${p.seq}: ${p.problem}`).join("; ") },
          ]}
          rows={check.chains.map((c) => ({ ...c, id: c.stream }))}
          empty="No records yet."
        />
      )}
    </>
  );
}

export function AuditScreen() {
  const [tab, setTab] = useState("Overview");
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [trailStream, setTrailStream] = useState("");

  useEffect(() => {
    apiGet("/Audit/summary/").then((res) => { setSummary(res); setError(""); })
      .catch((err) => setError(errText(err, "The audit portal is for auditors with an active engagement.")));
  }, []);

  const open = (key) => {
    if (key === "health") { setTab("Trail health"); return; }
    setTrailStream(TILE_FILTER[key] || "");
    setTab("Trail");
  };

  return (
    <Page>
      <div style={{ background: "#1f2a44", color: "#fff", padding: "6px 16px", borderRadius: 6, marginBottom: 12, fontSize: 13 }}>
        Audit mode: read-only. Everything you do here is recorded.
      </div>
      <PageHeader eyebrow="Audit" title={summary ? summary.company.name : "Audit"}
        subtitle={summary ? `Period ${when(summary.period.from)} to ${when(summary.period.to)}` : ""} />
      {error && <p className="ds-field__error">{error}</p>}
      {!error && (
        <>
          <div className="ds-toolbar" style={{ gap: 8, marginBottom: 16 }}>
            {TABS.map((t) => (
              <Button key={t} variant={tab === t ? undefined : "secondary"} size="sm" onClick={() => setTab(t)}>{t}</Button>
            ))}
          </div>
          {tab === "Overview" && <Overview summary={summary} onOpen={open} />}
          {tab === "Trail" && <Trail initialStream={trailStream} />}
          {tab === "Settings" && <Settings />}
          {tab === "Trail health" && <Health />}
        </>
      )}
    </Page>
  );
}

export default AuditScreen;
